import { useEffect, useId, useRef, type CSSProperties } from 'react';
import { cn } from '@/components/ui/cn';
import {
  closedSmoothPath,
  FLOW_ARCS,
  flowArcs,
  openSmoothPath,
  outline,
  stirArm,
  type LiquidFrame,
  type LiquidParams,
} from './liquid';

/*
  Жидкое тело: SVG-контур, пересчитываемый каждый кадр по радиальной функции (liquid.ts).

  Слои: аура (мягкий круг за телом) · тело (сплошной цвет, анимируется с темпом срочности)
  · объём (свет сверху-слева и тень снизу-справа, не зависят от цвета) · рельеф материала
  (поток, вихрь перемешивания, волны от удара — мягким смешиванием, обрезаны по контуру)
  · блик (эллипс по кривизне, статичный: он от источника света, а не от курсора).
  Контур один и тот же у тела, объёма и обрезки.

  Реакции на событие (docs/observer-design.md §4.7, «Финалы операции»): «готово» — новый цвет
  разливается из центра, затем один ровный пульс; «шаг» — тот же разлив зелёного с пульсом
  поменьше, после него возвращается рабочий цвет; «ошибка» — ёж: шипы выстреливают, дрожат и
  опадают, тело вздрагивает. Хорошая новость разливается спокойно, плохая бьёт сразу.

  Среда (притяжение к курсору) читается через функцию getEnv в кадровой петле,
  а не через состояние React: иначе каждое движение мыши перерисовывало бы страницу.
*/

export interface LiquidEnv {
  pull: { angle: number; strength: number };
}

/** Событие, на которое тело отвечает жестом: вид и момент (performance.now()). */
export interface LiquidReaction {
  kind: 'success' | 'step' | 'error';
  at: number;
}

interface LiquidBodyProps {
  params: LiquidParams;
  /** Диаметр тела в покое, px. */
  size: number;
  /** Цвет тела: любое CSS-значение, обычно токен. */
  color: string;
  /** Темп смены цвета, мс. */
  colorMs?: number;
  /** Момент удара «камня» (performance.now()); задаёт фазу всплеска. */
  splashAt?: number | null;
  /** Среда на текущий кадр. */
  getEnv?: () => LiquidEnv;
  /** Сколько волн идёт от одного удара, 1–4. */
  waveCount?: number;
  auraPulse?: boolean;
  /** Последнее событие: тело играет на него жест по времени от его момента. */
  reaction?: LiquidReaction | null;
  /** Держать шипы на полной высоте — чтобы подбирать их вид на стенде. */
  holdSpikes?: boolean;
  /** Тусклая аура: пока ошибка не снята. */
  auraDim?: boolean;
  className?: string;
}

/** Запас вокруг тела под выступы и ауру: svg больше тела в 1.8 раза. */
const BOX = 144;
const R = 40;
/** Волна от удара: длительность и пауза до второй волны. */
const WAVE_MS = 2000;
const WAVE_GAP_MS = 520;
/** Прогиб дуги: волна обтекает сферу, а не режет её прямой линией. */
const FLOW_SAG = 0.22;
/**
 * Дуга длиннее тела и обрезается живым контуром: там, где край вздувается волной, рельеф
 * доходит до самого края, а не обрывается внутри.
 */
const FLOW_OVERHANG = 1.25;
/**
 * Толщина рельефа везде — в единицах тела (радиус R = 40), а не в пикселях экрана: малое тело —
 * точная копия крупного, и на 32 px рельеф почти не виден, как и задумано. Числа подобраны
 * на крупном виде стенда (200 px, 2,5 px на единицу).
 *
 * Дуга потока — гребень: светлый склон сверху, тень снизу; ширина и сдвиг — в долях толщины
 * из параметров (flow.width), широкий штрих даёт мягкий край, узкий — сам гребень.
 */
const FLOW_STROKES: Array<{ width: number; opacity: number; shift: number; tone: 'light' | 'dark' }> = [
  { width: 0.6, opacity: 0.4, shift: -0.2, tone: 'light' },
  { width: 0.28, opacity: 0.75, shift: -0.2, tone: 'light' },
  { width: 0.6, opacity: 0.35, shift: 0.2, tone: 'dark' },
  { width: 0.28, opacity: 0.65, shift: 0.2, tone: 'dark' },
];
/**
 * Волна как рельеф материала, а не линия поверх: у гребня освещённый склон со стороны света
 * (сверху-слева, как у самого шара) и затенённый с противоположной; у впадины наоборот.
 * Штрих красится направленным градиентом «свет → тень», группа смешивается с телом мягким светом.
 * Волна растёт масштабом от центра, поэтому штрих не масштабируется вместе с ней, а толщина
 * пересчитывается в пиксели от размера тела.
 */
const WAVE_STROKES: Array<{ width: number; opacity: number; scale: number; kind: 'crest' | 'trough' }> = [
  { width: 4.8, opacity: 0.55, scale: 1, kind: 'crest' },
  { width: 2.4, opacity: 0.85, scale: 1, kind: 'crest' },
  { width: 3.6, opacity: 0.6, scale: 0.88, kind: 'trough' },
];
/** Аура при передаче данных: цикл стекания при скорости потока 1, с. */
const AURA_FLOW_S = 1.4;
/** Перемешивание: оборот вихря за 8 с при скорости 1; затягивается и отпускает раз в 6 с. */
const STIR_TURN_S = 8;
const STIR_BREATH_S = 6;
/** Сдвиг рельефа завитка к свету (сверху-слева) и от него, в единицах тела по каждой оси. */
const STIR_OFFSET = 1.3;
/**
 * Завиток как рельеф: светлый склон сдвинут к свету, тень — от него; широкий штрих даёт мягкий
 * край, узкий — гребень. Толщина в долях тела, а не в пикселях экрана: на 32 px завитки почти
 * не видны, и так задумано — на малом размере состояние несут силуэт, цвет и аура.
 */
const STIR_STROKES: Array<{ width: number; opacity: number; tone: 'light' | 'dark' }> = [
  { width: 6, opacity: 0.4, tone: 'light' },
  { width: 2.6, opacity: 0.75, tone: 'light' },
  { width: 6, opacity: 0.35, tone: 'dark' },
  { width: 2.6, opacity: 0.65, tone: 'dark' },
];
/** Два завитка; второй чуть смещён, короче и дышит со сдвигом — полная симметрия выглядит механикой. */
const STIR_ARMS = [
  { offset: 0, breath: 0, gain: 1, reach: 0.96 },
  { offset: Math.PI * 1.08, breath: 0.9, gain: 0.8, reach: 0.88 },
];

/** «Готово»: новый цвет за FLOOD_MS доходит из центра до края, потом пятно гаснет поверх уже нового цвета. */
const FLOOD_MS = 480;
const FLOOD_FADE_MS = 220;
/**
 * Накрыв тело, пятно дотягивается дальше края: его мягкий край уходит за контур, и старый цвет
 * не просвечивает ободком — ни на пульсе, ни пока у шага держится зелёный.
 */
const FLOOD_REACH_MS = 200;
const FLOOD_REACH = 0.5;
/** Кожа берёт новый цвет, когда разлив прошёл эту долю пути: до того под пятном старый цвет. */
const FLOOD_SKIN_AT = 0.8;
/** Ровный пульс без отскока: начинается, когда разлив почти у края. */
const PULSE_AT = 0.85;
const PULSE_MS = 600;
const PULSE_AMP = 0.12;
/**
 * «Шаг готов»: тот же разлив зелёного, что у «готово», но пульс меньше; после пульса и короткой
 * паузы зелёный плавно уходит и возвращается рабочий цвет — операция ещё идёт.
 */
const STEP_PULSE_AMP = 0.07;
const STEP_HOLD_MS = 200;
const STEP_RETURN_MS = 500;
/** «Ошибка»: выброс с перелётом, дрожь, опадание (его время — в параметрах шипов), колыхание. */
const BURST_MS = 100;
const BURST_OVERSHOOT = 0.12;
const TREMBLE_MS = 450;
const SETTLE_MS = 650;
const SETTLE_AMP = 0.04;
/** Тело вздрагивает: сдвиг в единицах тела (на 32 px это около 2 px), частота, длительность. */
const SHAKE_AMP = 4.5;
const SHAKE_HZ = 10;
const SHAKE_MS = 320;

const easeOut = (u: number) => 1 - Math.pow(1 - u, 3);
const easeInOut = (u: number) => (u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2);

/**
 * Ход реакции через ms после события: добавки к форме (LiquidFrame) и то, что рисуется поверх —
 * доля разлива (−1 — нет) и его угасание, сдвиг вздрагивания, вспышка ауры.
 */
function reactionAt(r: LiquidReaction | null, ms: number, spikes: LiquidParams['spikes'], hold: boolean) {
  const f: LiquidFrame = { pulse: 0, spikes: hold ? 1 : 0, jitter: 0, settle: 0 };
  const out = { f, flood: -1, floodReach: 0, floodFade: 1, shake: 0, aura: 0 };
  if (!r || ms < 0) return out;

  if (r.kind === 'success' || r.kind === 'step') {
    // «Готово»: пятно гаснет поверх уже нового цвета кожи. «Шаг»: кожа своего цвета не меняет,
    // зелёное пятно держится до конца пульса и короткой паузы, потом плавно уходит.
    const step = r.kind === 'step';
    const holdEnd = step ? FLOOD_MS * PULSE_AT + PULSE_MS + STEP_HOLD_MS : FLOOD_MS;
    const fadeMs = step ? STEP_RETURN_MS : FLOOD_FADE_MS;
    if (ms < holdEnd + fadeMs) {
      out.flood = Math.min(1, ms / FLOOD_MS);
      out.floodReach = Math.max(0, Math.min(1, (ms - FLOOD_MS) / FLOOD_REACH_MS));
      out.floodFade = ms <= holdEnd ? 1 : 1 - (ms - holdEnd) / fadeMs;
    }
    const u = (ms - FLOOD_MS * PULSE_AT) / PULSE_MS;
    if (u > 0 && u < 1) {
      const b = Math.sin(Math.PI * u);
      const amp = step ? STEP_PULSE_AMP : PULSE_AMP;
      f.pulse = amp * b;
      out.aura = (b * amp) / PULSE_AMP;
    }
  } else {
    const burstEnd = BURST_MS * 2;
    const trembleEnd = burstEnd + TREMBLE_MS;
    const fallMs = Math.max(100, spikes.fall * 1000);
    const fallEnd = trembleEnd + fallMs;
    let e = 0;
    if (ms < BURST_MS) e = (1 + BURST_OVERSHOOT) * easeOut(ms / BURST_MS);
    else if (ms < burstEnd) e = 1 + BURST_OVERSHOOT * (1 - (ms - BURST_MS) / BURST_MS);
    else if (ms < trembleEnd) {
      e = 1;
      f.jitter = spikes.tremble * 0.3 * (1 - (ms - burstEnd) / TREMBLE_MS);
    } else if (ms < fallEnd) e = 1 - easeInOut((ms - trembleEnd) / fallMs);
    f.spikes = Math.max(f.spikes, e);
    // Поверхность один раз мягко колыхнётся, когда шипы почти ушли.
    const su = (ms - (fallEnd - fallMs * 0.2)) / SETTLE_MS;
    if (su > 0 && su < 1) f.settle = SETTLE_AMP * Math.sin(Math.PI * 3 * su) * (1 - su);
    if (ms < SHAKE_MS)
      out.shake = SHAKE_AMP * Math.sin((Math.PI * 2 * SHAKE_HZ * ms) / 1000) * (1 - ms / SHAKE_MS);
  }
  return out;
}

export function LiquidBody({
  params,
  size,
  color,
  colorMs = 320,
  splashAt = null,
  getEnv,
  waveCount = 3,
  auraPulse = false,
  reaction = null,
  holdSpikes = false,
  auraDim = false,
  className,
}: LiquidBodyProps) {
  const id = useId();
  const shellRef = useRef<SVGGElement>(null);
  const auraRef = useRef<SVGCircleElement>(null);
  const floodRef = useRef<SVGCircleElement>(null);
  const floodRimRef = useRef<SVGCircleElement>(null);
  const glintRef = useRef<SVGEllipseElement>(null);
  const reactionRef = useRef(reaction);
  const holdRef = useRef(holdSpikes);
  const bodyRef = useRef<SVGPathElement>(null);
  const shadeRef = useRef<SVGPathElement>(null);
  const darkRef = useRef<SVGPathElement>(null);
  const clipRef = useRef<SVGPathElement>(null);
  const waveRefs = useRef<Array<SVGGElement | null>>([]);
  const flowRefs = useRef<Array<SVGGElement | null>>([]);
  const stirRefs = useRef<Array<SVGGElement | null>>([]);
  /** Активные волны: момент старта каждой. Пул из четырёх групп, включаются первые waveCount. */
  const waves = useRef<Array<number | null>>([null, null, null, null]);
  const waveCountRef = useRef(waveCount);
  const paramsRef = useRef(params);
  const splashRef = useRef(splashAt);
  const envRef = useRef(getEnv);

  useEffect(() => {
    reactionRef.current = reaction;
    holdRef.current = holdSpikes;
  }, [reaction, holdSpikes]);

  useEffect(() => {
    paramsRef.current = params;
    envRef.current = getEnv;
    waveCountRef.current = Math.max(1, Math.min(4, Math.round(waveCount)));
    if (splashAt && splashAt !== splashRef.current) {
      const n = waveCountRef.current;
      waves.current = [0, 1, 2, 3].map((i) => (i < n ? splashAt + i * WAVE_GAP_MS : null));
    }
    splashRef.current = splashAt;
  }, [params, splashAt, getEnv, waveCount]);

  useEffect(() => {
    let raf = 0;
    const t0 = performance.now();
    /** Поворот вихря копится по шагам, а не считается от t: ползунок скорости не даёт рывка. */
    let stirAngle = 0;
    let lastT = 0;
    const frame = () => {
      const now = performance.now();
      const t = (now - t0) / 1000;
      const dt = Math.min(0.1, Math.max(0, t - lastT));
      lastT = t;
      const env = envRef.current?.();
      const base = paramsRef.current;
      const p: LiquidParams = {
        ...base,
        radius: R,
        pull: env ? env.pull : base.pull,
        splash: {
          amp: base.splash.amp,
          phase: splashRef.current ? Math.max(0, (now - splashRef.current) / 1000) : 0,
        },
      };
      const r = reactionRef.current;
      const rx = reactionAt(r, r ? now - r.at : -1, base.spikes, holdRef.current);
      const contour = outline(p, t, rx.f);
      const d = closedSmoothPath(contour);
      bodyRef.current?.setAttribute('d', d);
      shadeRef.current?.setAttribute('d', d);
      darkRef.current?.setAttribute('d', d);
      clipRef.current?.setAttribute('d', d);

      // «Готово» и «шаг»: зелёное пятно растёт из центра, светлый фронт идёт чуть впереди и гаснет у края.
      const flood = floodRef.current;
      const rim = floodRimRef.current;
      if (rx.flood >= 0) {
        const fr = R * (1.2 * easeOut(rx.flood) + FLOOD_REACH * easeOut(rx.floodReach));
        flood?.setAttribute('r', fr.toFixed(2));
        flood?.setAttribute('opacity', rx.floodFade.toFixed(3));
        rim?.setAttribute('r', (fr * 0.9).toFixed(2));
        rim?.setAttribute('opacity', (0.55 * (1 - rx.flood) * rx.floodFade).toFixed(3));
      } else {
        flood?.setAttribute('opacity', '0');
        rim?.setAttribute('opacity', '0');
      }
      // Блик раздувается вместе с телом; тело вздрагивает целиком, вместе с аурой и бликом.
      glintRef.current?.setAttribute('transform', `scale(${(1 + rx.f.pulse).toFixed(4)}) rotate(-28)`);
      shellRef.current?.setAttribute('transform', rx.shake ? `translate(${rx.shake.toFixed(2)} 0)` : '');
      // Аура вспыхивает на пульсе «готово».
      const aura = auraRef.current;
      if (aura) {
        if (rx.aura > 0) {
          aura.style.scale = String(1 + 0.3 * rx.aura);
          aura.style.opacity = String(0.7 + 0.3 * rx.aura);
        } else if (aura.style.scale) {
          aura.style.removeProperty('scale');
          aura.style.removeProperty('opacity');
        }
      }

      // Поток: дуги-рельеф бегут по поверхности вниз или вверх, гаснут у полюсов. Высоты — те же,
      // по которым контур вздувается волной по краю (flowArcs), поэтому гребень и выпуклость совпадают.
      const arcs = flowArcs(base.flow, t);
      for (let i = 0; i < FLOW_ARCS; i++) {
        const g = flowRefs.current[i];
        if (!g) continue;
        const a = arcs[i];
        if (!a) {
          g.setAttribute('opacity', '0');
          continue;
        }
        const y = a.y * R;
        const halfWidth = Math.sqrt(Math.max(0, R * R - y * y)) * FLOW_OVERHANG;
        const sag = FLOW_SAG * R * (0.6 + 0.4 * a.fade);
        const ad = `M ${(-halfWidth).toFixed(2)} ${y.toFixed(2)} Q 0 ${(y + sag).toFixed(2)} ${halfWidth.toFixed(2)} ${y.toFixed(2)}`;
        for (const path of g.querySelectorAll('path')) path.setAttribute('d', ad);
        g.setAttribute('opacity', (a.fade * base.flow.strength).toFixed(3));
      }

      // Перемешивание: завитки поворачиваются, то затягиваясь, то отпуская почти до гладкой
      // поверхности; вместе с закруткой дышит и сила рельефа.
      const stir = base.stir;
      if (stir.dir === 0 || stir.strength <= 0) {
        for (const g of stirRefs.current) g?.setAttribute('opacity', '0');
      } else {
        stirAngle += (stir.dir * stir.speed * 2 * Math.PI * dt) / STIR_TURN_S;
        STIR_ARMS.forEach((arm, j) => {
          const g = stirRefs.current[j];
          if (!g) return;
          const b = 0.5 - 0.5 * Math.cos((2 * Math.PI * t) / STIR_BREATH_S + arm.breath);
          const twist = stir.twist * (0.45 + 0.55 * b) * 2 * Math.PI;
          const ad = openSmoothPath(stirArm(contour, stirAngle + arm.offset, twist, stir.dir, arm.reach));
          for (const path of g.querySelectorAll('path')) path.setAttribute('d', ad);
          g.setAttribute('opacity', (stir.strength * arm.gain * (0.25 + 0.75 * b)).toFixed(3));
        });
      }

      // Волны от удара: уменьшенные копии ТЕКУЩЕГО контура, расходятся от центра к краю.
      // Масштаб — атрибутом transform относительно (0,0): контур центрирован, поэтому
      // точка отсчёта всегда в середине тела, какой бы формы оно ни было.
      waves.current.forEach((start, i) => {
        const g = waveRefs.current[i];
        if (!g) return;
        if (start === null || now < start) {
          g.setAttribute('opacity', '0');
          return;
        }
        const u = (now - start) / WAVE_MS;
        if (u >= 1) {
          waves.current[i] = null;
          g.setAttribute('opacity', '0');
          return;
        }
        const ease = 1 - Math.pow(1 - u, 3); // быстро в начале, замедляется у края
        const k = 0.08 + 0.92 * ease;
        const fade = Math.sin(Math.PI * Math.min(1, u * 1.15)); // мягкий вход и уход
        g.setAttribute('opacity', fade.toFixed(3));
        for (const path of g.querySelectorAll('path')) {
          path.setAttribute('d', d);
          const own = Number(path.dataset.scale ?? '1');
          path.setAttribute('transform', `scale(${(k * own).toFixed(4)})`);
        }
      });
    };
    const tick = () => {
      frame();
      raf = requestAnimationFrame(tick);
    };
    // Первый контур сразу и страховка таймером: в скрытой вкладке кадров нет,
    // а тело не должно оставаться пустым до возвращения на вкладку.
    frame();
    const safety = window.setInterval(() => {
      if (document.hidden) frame();
    }, 250);
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      window.clearInterval(safety);
    };
  }, []);

  const px = size * (BOX / (R * 2));
  /** Пикселей на единицу тела: для штрихов, которые не масштабируются вместе с волной. */
  const pxPerUnit = size / (R * 2);
  /** Направление ауры: при передаче данных она течёт, иначе пульсирует или стоит. */
  const auraFlow = params.flow.strength > 0 ? params.flow.dir : 0;
  /** При «готово» кожа держит старый цвет, пока его не накроет разлив: новый цвет приходит из центра. */
  const colorDelay = reaction?.kind === 'success' ? FLOOD_MS * FLOOD_SKIN_AT : 0;

  return (
    <svg
      width={px}
      height={px}
      viewBox={`${-BOX / 2} ${-BOX / 2} ${BOX} ${BOX}`}
      aria-hidden
      data-liquid=""
      style={
        { '--body': color, '--color-ms': `${colorMs}ms`, '--color-delay': `${colorDelay}ms` } as CSSProperties
      }
      className={cn('block overflow-visible', className)}
    >
      <defs>
        <radialGradient id={`${id}-aura`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="var(--body)" stopOpacity="0.5" />
          <stop offset="45%" stopColor="var(--body)" stopOpacity="0.18" />
          <stop offset="100%" stopColor="var(--body)" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${id}-light`} cx="34%" cy="30%" r="70%">
          <stop offset="0%" stopColor="var(--liquid-light)" stopOpacity="0.38" />
          <stop offset="55%" stopColor="var(--liquid-light)" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${id}-dark`} cx="70%" cy="80%" r="75%">
          <stop offset="0%" stopColor="var(--liquid-shade)" stopOpacity="0.42" />
          <stop offset="65%" stopColor="var(--liquid-shade)" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${id}-glint`} cx="46%" cy="46%" r="50%">
          <stop offset="0%" stopColor="var(--liquid-light)" stopOpacity="0.92" />
          <stop offset="30%" stopColor="var(--liquid-light)" stopOpacity="0.5" />
          <stop offset="60%" stopColor="var(--liquid-light)" stopOpacity="0.12" />
          <stop offset="100%" stopColor="var(--liquid-light)" stopOpacity="0" />
        </radialGradient>
        {/* Обрезка по телу: волны от удара не выходят за контур */}
        <clipPath id={`${id}-clip`}>
          <path ref={clipRef} />
        </clipPath>
        {/* Рельеф волны: свет сверху-слева, тень снизу-справа — то же направление, что у объёма */}
        <linearGradient id={`${id}-crest`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--liquid-light)" stopOpacity="0.95" />
          <stop offset="50%" stopColor="var(--liquid-light)" stopOpacity="0" />
          <stop offset="100%" stopColor="var(--liquid-shade)" stopOpacity="0.9" />
        </linearGradient>
        <linearGradient id={`${id}-trough`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--liquid-shade)" stopOpacity="0.85" />
          <stop offset="50%" stopColor="var(--liquid-shade)" stopOpacity="0" />
          <stop offset="100%" stopColor="var(--liquid-light)" stopOpacity="0.8" />
        </linearGradient>
        {/* Завитки растворяются к центру и к краю: у них нет концов, поэтому они не читаются полосами */}
        <radialGradient id={`${id}-stir-fade`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="var(--liquid-shade)" />
          <stop offset="12%" stopColor="var(--liquid-shade)" />
          <stop offset="34%" stopColor="var(--liquid-light)" />
          <stop offset="52%" stopColor="var(--liquid-light)" />
          <stop offset="80%" stopColor="var(--liquid-shade)" />
        </radialGradient>
        {/* Постоянный рисунок тише разового: рельеф вихря и потока чуть размыт, волны от удара чёткие.
            Область фильтра — весь холст: у плоской дуги рамка по геометрии тоньше штриха и срезала бы его */}
        <filter
          id={`${id}-soft`}
          filterUnits="userSpaceOnUse"
          x={-BOX / 2}
          y={-BOX / 2}
          width={BOX}
          height={BOX}
        >
          <feGaussianBlur stdDeviation="1.1" />
        </filter>
        <mask
          id={`${id}-stir-mask`}
          maskUnits="userSpaceOnUse"
          x={-BOX / 2}
          y={-BOX / 2}
          width={BOX}
          height={BOX}
        >
          <circle r={R * 1.15} fill={`url(#${id}-stir-fade)`} />
        </mask>
        {/* Разлив «готово»: сплошной новый цвет с мягким краем */}
        <radialGradient id={`${id}-flood`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="var(--body)" />
          <stop offset="78%" stopColor="var(--body)" />
          <stop offset="100%" stopColor="var(--body)" stopOpacity="0" />
        </radialGradient>
        {/* Разлив «шаг готов»: тот же, но всегда зелёный — кожа держит рабочий цвет, к нему и вернётся */}
        <radialGradient id={`${id}-flood-step`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="var(--success)" />
          <stop offset="78%" stopColor="var(--success)" />
          <stop offset="100%" stopColor="var(--success)" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Всё тело вместе: при ошибке вздрагивает целиком, с аурой и бликом */}
      <g ref={shellRef}>
        <circle
          ref={auraRef}
          r={R * 1.55}
          fill={`url(#${id}-aura)`}
          opacity={auraDim ? 0.25 : undefined}
          style={auraFlow !== 0 ? { animationDuration: `${AURA_FLOW_S / params.flow.speed}s` } : undefined}
          className={cn(
            'origin-center',
            auraFlow === 1 && 'animate-companion-aura-down motion-reduce:animate-none',
            auraFlow === -1 && 'animate-companion-aura-up motion-reduce:animate-none',
            auraFlow === 0 &&
              !auraDim &&
              (auraPulse ? 'animate-companion-aura motion-reduce:animate-none' : 'opacity-70'),
          )}
        />

        {/* Тело: сплошной цвет, переход по --color-ms (при «готово» — с задержкой под разлив) */}
        <path ref={bodyRef} data-part="skin" fill="var(--body)" />
        {/* «Готово» и «шаг»: свет из центра под объёмом и бликом — зеленеет сам материал */}
        <g clipPath={`url(#${id}-clip)`} className="motion-reduce:hidden">
          <circle
            ref={floodRef}
            r={0}
            opacity={0}
            fill={reaction?.kind === 'step' ? `url(#${id}-flood-step)` : `url(#${id}-flood)`}
          />
          <circle
            ref={floodRimRef}
            r={0}
            opacity={0}
            fill="none"
            stroke="var(--liquid-light)"
            strokeWidth={6}
            filter={`url(#${id}-soft)`}
            style={{ mixBlendMode: 'soft-light' }}
          />
        </g>
        {/* Объём: свет и тень тем же контуром */}
        <path ref={shadeRef} fill={`url(#${id}-light)`} />
        <path ref={darkRef} fill={`url(#${id}-dark)`} />
        {/* Поток: дуги-рельеф по поверхности при передаче данных; управляются в кадровом цикле */}
        <g
          clipPath={`url(#${id}-clip)`}
          style={{ mixBlendMode: 'soft-light' }}
          className="motion-reduce:hidden"
        >
          {Array.from({ length: FLOW_ARCS }, (_, i) => (
            <g
              key={i}
              opacity={0}
              filter={`url(#${id}-soft)`}
              ref={(el) => {
                flowRefs.current[i] = el;
              }}
            >
              {FLOW_STROKES.map((st, k) => (
                <path
                  key={k}
                  fill="none"
                  stroke={st.tone === 'light' ? 'var(--liquid-light)' : 'var(--liquid-shade)'}
                  strokeOpacity={st.opacity}
                  strokeWidth={st.width * params.flow.width}
                  strokeLinecap="round"
                  transform={`translate(0 ${st.shift * params.flow.width})`}
                />
              ))}
            </g>
          ))}
        </g>

        {/* Вихрь перемешивания («думает»): два завитка-рельефа внутри тела; управляются в кадровом цикле */}
        <g
          clipPath={`url(#${id}-clip)`}
          mask={`url(#${id}-stir-mask)`}
          style={{ mixBlendMode: 'soft-light' }}
          className="motion-reduce:hidden"
        >
          {STIR_ARMS.map((_, j) => (
            <g
              key={j}
              opacity={0}
              filter={`url(#${id}-soft)`}
              ref={(el) => {
                stirRefs.current[j] = el;
              }}
            >
              {STIR_STROKES.map((st, k) => (
                <path
                  key={k}
                  fill="none"
                  stroke={st.tone === 'light' ? 'var(--liquid-light)' : 'var(--liquid-shade)'}
                  strokeOpacity={st.opacity}
                  strokeWidth={st.width}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  transform={
                    st.tone === 'light'
                      ? `translate(${-STIR_OFFSET} ${-STIR_OFFSET})`
                      : `translate(${STIR_OFFSET} ${STIR_OFFSET})`
                  }
                />
              ))}
            </g>
          ))}
        </g>

        {/* Волны по воде: копии текущего контура, обрезаны по телу; управляются в кадровом цикле */}
        <g
          clipPath={`url(#${id}-clip)`}
          style={{ mixBlendMode: 'soft-light' }}
          className="motion-reduce:hidden"
        >
          {[0, 1, 2, 3].map((i) => (
            <g
              key={i}
              opacity={0}
              ref={(el) => {
                waveRefs.current[i] = el;
              }}
            >
              {WAVE_STROKES.map((st, j) => (
                <path
                  key={j}
                  data-scale={st.scale}
                  fill="none"
                  stroke={`url(#${id}-${st.kind})`}
                  strokeOpacity={st.opacity}
                  strokeWidth={Number((st.width * pxPerUnit).toFixed(2))}
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
            </g>
          ))}
        </g>
        {/* Блик по кривизне: статичный, от источника света, а не от курсора; раздувается вместе с телом */}
        <ellipse
          ref={glintRef}
          cx={-13}
          cy={-15}
          rx={13}
          ry={8}
          transform="rotate(-28)"
          fill={`url(#${id}-glint)`}
          style={{ mixBlendMode: 'screen' }}
        />
      </g>
    </svg>
  );
}
